/* eslint-disable react/no-unknown-property -- Firebolt потребляет css-проп при сборке. */

// Общие детали голосовых экранов (VOICE ENHANCEMENT и VOICE HEALTH).
// Оба экрана из одного пакета макетов, у них общая шапка, панели, кнопки и
// индикаторы — держим их здесь, чтобы экраны не разъехались между собой.
//
// Ни одного цвета мимо theme.js: правило проекта — единственный источник токенов.

import { css } from '@firebolt-dev/css'
import { XIcon, InfoIcon, ShieldCheckIcon } from 'lucide-react'

import { cls } from './cls'
import { COLORS, RADII, space, MOTION, SHADOWS, GLASS, TYPE, ICON_SIZES, INPUT_HEIGHTS, TOUCH_MIN } from '../theme'

// ─── Оболочка экрана: полноэкранный оверлей с шапкой ─────────────────────────

export function ScreenShell({ label, title, subtitle, meta, onClose, children, ariaLabel }) {
  return (
    <div
      role='dialog'
      aria-modal='true'
      aria-label={ariaLabel || title}
      css={css`
        position: fixed;
        inset: 0;
        z-index: 50;
        display: flex;
        flex-direction: column;
        background: ${COLORS.bgDeep};
        backdrop-filter: ${GLASS.base};
        color: ${COLORS.text};
        font-family: inherit;
      `}
    >
      <header
        css={css`
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: ${space(3)};
          padding: ${space(2)} ${space(3)};
          border-bottom: 1px solid ${COLORS.border};
          background: ${COLORS.bgPanel};

          @media (max-width: 1100px) {
            flex-wrap: wrap;
            gap: ${space(1)};
            padding: ${space(1)} ${space(2)};
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            line-height: 1;
          `}
        >
          <span
            css={css`
              font-size: ${TYPE.title.size};
              font-weight: ${TYPE.title.weight};
              letter-spacing: ${TYPE.title.spacing};
            `}
          >
            TESTCS
          </span>
          <span
            css={css`
              font-size: 0.55rem;
              letter-spacing: 0.42em;
              color: ${COLORS.textSecondary};
              margin-top: 3px;
            `}
          >
            {label || 'WORLD'}
          </span>
        </div>

        <div
          css={css`
            flex: 1 1 auto;
            text-align: center;
            min-width: 200px;
          `}
        >
          <div
            css={css`
              font-size: 1.05rem;
              font-weight: 700;
              letter-spacing: 0.14em;
              color: ${COLORS.cyan};
            `}
          >
            {title}
          </div>
          {subtitle && (
            <div
              css={css`
                font-size: ${TYPE.caption.size};
                letter-spacing: ${TYPE.caption.spacing};
                color: ${COLORS.textSecondary};
                margin-top: 2px;
              `}
            >
              {subtitle}
            </div>
          )}
        </div>

        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${space(2)};
            flex: 0 0 auto;
          `}
        >
          {meta}
          <button
            type='button'
            aria-label='Закрыть'
            onClick={onClose}
            css={css`
              appearance: none;
              background: transparent;
              border: 1px solid ${COLORS.border};
              border-radius: ${RADII.md};
              color: ${COLORS.textSecondary};
              width: ${TOUCH_MIN}px;
              height: 36px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: background ${MOTION.hover}, color ${MOTION.hover};
              &:hover {
                background: ${COLORS.errorSoft};
                color: ${COLORS.error};
              }
              &:focus-visible {
                outline: none;
                box-shadow: ${SHADOWS.focus};
              }
            `}
          >
            <XIcon size={ICON_SIZES.sm} />
          </button>
        </div>
      </header>

      {children}
    </div>
  )
}

// Обещание продукта в шапке: голос не подменяем
export function IdentityBadge({ title, subtitle }) {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${space(1)};
        padding: ${space(0)} ${space(1)};
        border: 1px solid ${COLORS.successSoft};
        border-radius: ${RADII.lg};
        background: ${COLORS.successSoft};
        max-width: 280px;
      `}
    >
      <ShieldCheckIcon size={ICON_SIZES.md} color={COLORS.success} />
      <div
        css={css`
          line-height: 1.25;
        `}
      >
        <div
          css={css`
            font-size: 0.68rem;
            font-weight: 700;
            letter-spacing: 0.12em;
            color: ${COLORS.success};
          `}
        >
          {title}
        </div>
        <div
          css={css`
            font-size: 0.68rem;
            color: ${COLORS.textSecondary};
          `}
        >
          {subtitle}
        </div>
      </div>
    </div>
  )
}

// Показатель в шапке: иконка + подпись + значение
export function MetaStat({ icon, label, value, tone }) {
  const color = tone === 'success' ? COLORS.success : tone === 'error' ? COLORS.error : COLORS.text
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: ${space(0)};
        white-space: nowrap;
      `}
    >
      {icon}
      <div
        css={css`
          line-height: 1.2;
        `}
      >
        <div
          css={css`
            font-size: 0.62rem;
            letter-spacing: 0.16em;
            color: ${COLORS.textSecondary};
          `}
        >
          {label}
        </div>
        <div
          css={css`
            font-size: 0.74rem;
            font-weight: 650;
            color: ${color};
          `}
        >
          {value}
        </div>
      </div>
    </div>
  )
}

// ─── Панель с заголовком ─────────────────────────────────────────────────────

export function Panel({ title, info, right, children, flush, className }) {
  return (
    <section
      className={className}
      css={css`
        border: 1px solid ${COLORS.border};
        border-radius: ${RADII.xl};
        background: ${COLORS.bgPanelLight};
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
      `}
    >
      {(title || right) && (
        <header
          css={css`
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            gap: ${space(1)};
            padding: ${space(1)} ${space(2)};
          `}
        >
          <span
            css={css`
              font-size: ${TYPE.section.size};
              font-weight: ${TYPE.section.weight};
              letter-spacing: ${TYPE.section.spacing};
              color: ${COLORS.text};
            `}
          >
            {title}
          </span>
          {info && <InfoDot text={info} />}
          {right && (
            <span
              css={css`
                margin-left: auto;
                display: flex;
                align-items: center;
                gap: ${space(1)};
              `}
            >
              {right}
            </span>
          )}
        </header>
      )}
      <div
        css={css`
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          padding: ${flush ? '0' : `0 ${space(2)} ${space(2)}`};
        `}
      >
        {children}
      </div>
    </section>
  )
}

export function InfoDot({ text }) {
  return (
    <span
      title={text}
      aria-label={text}
      css={css`
        display: inline-flex;
        color: ${COLORS.textSecondary};
        cursor: help;
      `}
    >
      <InfoIcon size={ICON_SIZES.xs} />
    </span>
  )
}

export function SubLabel({ children }) {
  return (
    <div
      css={css`
        font-size: ${TYPE.caption.size};
        letter-spacing: ${TYPE.caption.spacing};
        color: ${COLORS.textSecondary};
        margin-bottom: ${space(0)};
      `}
    >
      {children}
    </div>
  )
}

// ─── Кнопка ──────────────────────────────────────────────────────────────────

export function Btn({ variant = 'secondary', size = 'medium', full, children, disabled, ...rest }) {
  const palette = {
    primary: { bg: COLORS.cyan, fg: COLORS.bgDeep, border: COLORS.cyan, hover: COLORS.cyanHover },
    secondary: { bg: 'transparent', fg: COLORS.text, border: COLORS.border, hover: COLORS.cyanSoft },
    ghost: { bg: 'transparent', fg: COLORS.textSecondary, border: 'transparent', hover: COLORS.cyanSoft },
    accent: { bg: COLORS.cyanSoft, fg: COLORS.cyan, border: COLORS.borderStrong, hover: COLORS.cyanSoft },
    destructive: { bg: 'transparent', fg: COLORS.error, border: COLORS.errorSoft, hover: COLORS.errorSoft },
    success: { bg: COLORS.successSoft, fg: COLORS.success, border: COLORS.successSoft, hover: COLORS.successSoft },
  }[variant]

  return (
    <button
      type='button'
      disabled={disabled}
      css={css`
        appearance: none;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: ${space(0)};
        width: ${full ? '100%' : 'auto'};
        min-height: ${INPUT_HEIGHTS[size] || INPUT_HEIGHTS.medium}px;
        padding: 0 ${space(2)};
        border-radius: ${RADII.lg};
        border: 1px solid ${palette.border};
        background: ${palette.bg};
        color: ${palette.fg};
        font-family: inherit;
        font-size: 0.76rem;
        font-weight: 650;
        letter-spacing: 0.1em;
        cursor: pointer;
        transition: background ${MOTION.hover}, color ${MOTION.hover}, border-color ${MOTION.hover};

        &:hover:not(:disabled) {
          background: ${palette.hover};
          ${variant === 'primary' ? `color: ${COLORS.bgDeep};` : ''}
        }
        &:focus-visible {
          outline: none;
          box-shadow: ${SHADOWS.focus};
        }
        &:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
      `}
      {...rest}
    >
      {children}
    </button>
  )
}

// ─── Переключатель ───────────────────────────────────────────────────────────

export function Toggle({ checked, onChange, label, hint, icon, disabled }) {
  return (
    <label
      css={css`
        display: flex;
        align-items: flex-start;
        gap: ${space(1)};
        padding: ${space(1)};
        border: 1px solid ${COLORS.border};
        border-radius: ${RADII.lg};
        cursor: ${disabled ? 'not-allowed' : 'pointer'};
        opacity: ${disabled ? 0.5 : 1};
        transition: border-color ${MOTION.hover};
        &:hover {
          border-color: ${disabled ? COLORS.border : COLORS.borderStrong};
        }
      `}
    >
      {icon && (
        <span
          css={css`
            color: ${COLORS.textSecondary};
            margin-top: 2px;
          `}
        >
          {icon}
        </span>
      )}
      <span
        css={css`
          flex: 1 1 auto;
          min-width: 0;
        `}
      >
        <span
          css={css`
            display: block;
            font-size: 0.74rem;
            font-weight: 650;
            letter-spacing: 0.08em;
          `}
        >
          {label}
        </span>
        {hint && (
          <span
            css={css`
              display: block;
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
              margin-top: 2px;
            `}
          >
            {hint}
          </span>
        )}
      </span>
      <input
        type='checkbox'
        checked={!!checked}
        disabled={disabled}
        onChange={event => onChange?.(event.target.checked)}
        css={css`
          position: absolute;
          opacity: 0;
          pointer-events: none;
        `}
      />
      <span
        aria-hidden='true'
        css={css`
          flex: 0 0 auto;
          width: 40px;
          height: 22px;
          border-radius: ${RADII.round};
          background: ${checked ? COLORS.success : COLORS.disabled};
          position: relative;
          transition: background ${MOTION.hover};
          margin-top: 2px;
          &::after {
            content: '';
            position: absolute;
            top: 3px;
            left: ${checked ? '21px' : '3px'};
            width: 16px;
            height: 16px;
            border-radius: ${RADII.round};
            background: ${COLORS.text};
            transition: left ${MOTION.hover};
          }
        `}
      />
    </label>
  )
}

// ─── Радио-выбор (режимы согласия) ───────────────────────────────────────────

export function RadioRow({ checked, onChange, label, hint, warn, recommended }) {
  return (
    <label
      css={css`
        display: flex;
        align-items: flex-start;
        gap: ${space(1)};
        padding: ${space(1)};
        border: 1px solid ${checked ? COLORS.borderStrong : COLORS.border};
        border-radius: ${RADII.lg};
        background: ${checked ? COLORS.cyanSoft : 'transparent'};
        cursor: pointer;
        transition: border-color ${MOTION.hover}, background ${MOTION.hover};
        &:hover {
          border-color: ${COLORS.borderStrong};
        }
      `}
    >
      <input
        type='radio'
        checked={!!checked}
        onChange={() => onChange?.()}
        css={css`
          position: absolute;
          opacity: 0;
          pointer-events: none;
        `}
      />
      <span
        aria-hidden='true'
        css={css`
          flex: 0 0 auto;
          width: 18px;
          height: 18px;
          margin-top: 2px;
          border-radius: ${RADII.round};
          border: 2px solid ${checked ? COLORS.cyan : COLORS.disabled};
          display: flex;
          align-items: center;
          justify-content: center;
          &::after {
            content: '';
            width: 8px;
            height: 8px;
            border-radius: ${RADII.round};
            background: ${checked ? COLORS.cyan : 'transparent'};
          }
        `}
      />
      <span
        css={css`
          flex: 1 1 auto;
          min-width: 0;
        `}
      >
        <span
          css={css`
            display: block;
            font-size: 0.74rem;
            font-weight: 650;
            letter-spacing: 0.08em;
          `}
        >
          {label}
          {recommended && (
            <span
              css={css`
                color: ${COLORS.textSecondary};
                font-weight: 500;
                letter-spacing: 0.02em;
                margin-left: ${space(0)};
              `}
            >
              (Recommended)
            </span>
          )}
        </span>
        {hint && (
          <span
            css={css`
              display: block;
              font-size: ${TYPE.caption.size};
              color: ${COLORS.textSecondary};
              margin-top: 2px;
            `}
          >
            {hint}
          </span>
        )}
        {warn && (
          <span
            css={css`
              display: block;
              font-size: ${TYPE.caption.size};
              color: ${COLORS.warning};
              margin-top: 2px;
            `}
          >
            ⚠ {warn}
          </span>
        )}
      </span>
    </label>
  )
}

// ─── Волна: столбики уровня ──────────────────────────────────────────────────

export function Bars({ values, color = COLORS.success, height = 56, dim = false }) {
  return (
    <div
      aria-hidden='true'
      css={css`
        display: flex;
        align-items: center;
        gap: 2px;
        height: ${height}px;
        opacity: ${dim ? 0.45 : 1};
      `}
    >
      {values.map((v, i) => (
        <span
          key={i}
          css={css`
            flex: 1 1 auto;
            min-width: 1px;
            height: ${Math.max(2, v * height)}px;
            background: ${color};
            border-radius: ${RADII.xs};
            transition: height 0.06s linear;
          `}
        />
      ))}
    </div>
  )
}

// ─── Полоска уровня с цветовыми зонами ───────────────────────────────────────

export function LevelBar({ value, tone = 'success', height = 10 }) {
  const color = { success: COLORS.success, warning: COLORS.warning, error: COLORS.error, muted: COLORS.disabled }[tone]
  return (
    <div
      css={css`
        height: ${height}px;
        border-radius: ${RADII.xs};
        background: ${COLORS.bgDeep};
        border: 1px solid ${COLORS.border};
        overflow: hidden;
      `}
    >
      <div
        css={css`
          height: 100%;
          width: ${Math.max(0, Math.min(1, value)) * 100}%;
          background: ${color};
          transition: width 0.08s linear;
        `}
      />
    </div>
  )
}

// ─── Показатель: подпись сверху, значение снизу ──────────────────────────────

export function Stat({ label, value, tone }) {
  const color = { success: COLORS.success, warning: COLORS.warning, error: COLORS.error }[tone] || COLORS.text
  return (
    <div
      css={css`
        min-width: 0;
      `}
    >
      <div
        css={css`
          font-size: 0.6rem;
          letter-spacing: 0.14em;
          color: ${COLORS.textSecondary};
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
      >
        {label}
      </div>
      <div
        css={css`
          font-size: 1.05rem;
          font-weight: 700;
          color: ${color};
          margin-top: 2px;
        `}
      >
        {value}
      </div>
    </div>
  )
}

// ─── Нижняя панель действий ──────────────────────────────────────────────────

export function ActionBar({ left, center, right }) {
  return (
    <footer
      css={css`
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: ${space(2)};
        padding: ${space(2)} ${space(3)};
        border-top: 1px solid ${COLORS.border};
        background: ${COLORS.bgPanel};

        @media (max-width: 900px) {
          flex-wrap: wrap;
          padding: ${space(1)} ${space(2)};
        }
      `}
    >
      <div
        css={css`
          flex: 0 0 auto;
        `}
      >
        {left}
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${space(1)};
          font-size: ${TYPE.caption.size};
          color: ${COLORS.textSecondary};
          text-align: center;
        `}
      >
        {center}
      </div>
      <div
        css={css`
          flex: 0 0 auto;
          display: flex;
          align-items: center;
          gap: ${space(1)};
        `}
      >
        {right}
      </div>
    </footer>
  )
}

// Чип-подсказка рядом с участником
export function Chip({ children, tone = 'muted', icon }) {
  const color = { success: COLORS.success, warning: COLORS.warning, error: COLORS.error, muted: COLORS.textSecondary }[tone]
  return (
    <span
      className={cls('voice-chip')}
      css={css`
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 3px ${space(0)};
        border: 1px solid ${COLORS.border};
        border-radius: ${RADII.pill};
        font-size: 0.66rem;
        color: ${color};
        white-space: nowrap;
      `}
    >
      {icon}
      {children}
    </span>
  )
}
